import { css } from "@emotion/react";
import { Panel, PanelContent } from "src/components/Panel/Panel.tsx";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { useQuery } from "@tanstack/react-query";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useTranslation } from "react-i18next";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { EmptyState } from "src/components/EmptyState/EmptyState.tsx";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { formatPrice } from "@exonn/utils";
import { FormV2 } from "src/components/FormV2/FormV2.tsx";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

await loadTranslations(TranslationNamespaceEnum.BillBalance);

export const Balance = () => {
  const { t } = useTranslation(TranslationNamespaceEnum.BillBalance);
  const api = useExonnApi();

  const {
    data: balanceData,
    refetch,
    isLoading,
    isRefetching,
    isError: isBalanceError,
  } = useQuery({
    queryFn: async ({ signal }) => {
      return (
        await api.bill.getBalance(undefined, null, {
          signal,
        })
      ).data;
    },
    queryKey: ["balance"],
  });

  return (
    <FormV2.Root>
      <LoadingOverlay.Root isLoading={isLoading || isRefetching}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {t("advices.pleaseWait", {
            ns: TranslationNamespaceEnum.Common,
          })}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
      <FormV2.Inner>
        <FormV2.Toolbar>
          <IconButtonV2
            skin={"info"}
            size={"sm"}
            variant={"outlined"}
            onClick={() => refetch()}
          >
            <i className={"icon-15-update"} />
          </IconButtonV2>
        </FormV2.Toolbar>
        <FormV2.Grid>
          <div css={panelsContainer}>
            <Panel style={{ minHeight: 0, flexShrink: 0 }}>
              {isBalanceError ? (
                <div css={errorContainer}>
                  <EmptyState
                    type={"warning"}
                    title={t("panel.balance.error")}
                  />
                </div>
              ) : (
                <>
                  <Panel.Header>
                    <Panel.Title>{t("panel.balance.title")}</Panel.Title>
                  </Panel.Header>
                  <PanelContent>
                    <span css={balanceLabel}>
                      <Skeleton isLoading={isLoading}>
                        {/*{balanceData?.data.balance &&*/}
                        {/*balanceData?.data.balance.length > 0*/}
                        {/*  ? balanceData?.data.balance*/}
                        {/*  : "0.00"}{" "}*/}
                        {formatPrice(
                          Number(
                            balanceData?.data.balance
                              ? balanceData.data.balance
                              : 0,
                          ),
                          {
                            symbol: "€",
                          },
                        )}
                      </Skeleton>
                    </span>
                  </PanelContent>
                </>
              )}
            </Panel>
            <Panel style={{ minHeight: 0, flexShrink: 0 }}>
              {!isBalanceError ? (
                <>
                  <Panel.Header>
                    <Panel.Title>{t("panel.top_up.title")}</Panel.Title>
                  </Panel.Header>
                  <PanelContent>
                    <div css={item}>
                      <div css={left}>{t("panel.top_up.payee")}</div>
                      <Skeleton isLoading={isLoading}>
                        <div css={right}>{balanceData?.data.payee}</div>
                      </Skeleton>
                    </div>
                    <div css={item}>
                      <div css={left}>{t("panel.top_up.iban")}</div>
                      <Skeleton isLoading={isLoading}>
                        <div css={right}>{balanceData?.data.iban}</div>
                      </Skeleton>
                    </div>
                    <div css={item}>
                      <div css={left}>{t("panel.top_up.swift_bic")}</div>
                      <Skeleton isLoading={isLoading}>
                        <div css={right}>{balanceData?.data.swift_bic}</div>
                      </Skeleton>
                    </div>
                    <div css={item}>
                      <div css={left}>{t("panel.top_up.intended")}</div>
                      <Skeleton isLoading={isLoading}>
                        <div css={right}>{balanceData?.data?.customnumber}</div>
                      </Skeleton>
                    </div>
                  </PanelContent>
                </>
              ) : (
                <div css={errorContainer}>
                  <EmptyState
                    type={"warning"}
                    title={t("panel.top_up.error")}
                  />
                </div>
              )}
            </Panel>
          </div>
        </FormV2.Grid>
      </FormV2.Inner>
    </FormV2.Root>
  );
};

const item = (theme: ExactTheme) => css`
  width: 494px;
  padding: 10px 0;
  border-bottom: 1px solid ${theme.colorsV2.grey[100]};
  display: flex;
`;
const left = css`
  width: 285px;
  padding-left: 10px;
`;
const right = css`
  padding-left: 10px;
  min-width: 100px;
`;

const balanceLabel = (theme: ExactTheme) => css`
  font-size: ${theme.fontSizesV2.h1};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colorsV2.blue[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
`;

const panelsContainer = css`
  width: 524px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 15px;
`;

const errorContainer = css`
  padding: 15px;
`;
